import { can } from "modules/settings/accessRoles";
import {
  Box,
  Button as MuiButton,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import TableContainer from "@mui/material/TableContainer";
import EditIcon from "@mui/icons-material/Edit";
import Skeleton from "@mui/material/Skeleton";
import { spacing } from "@mui/system";
import PropTypes from "prop-types";
import React from "react";
import moment from "moment";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";
import style from "../../property/components/styling/style";
import PropertyUtilityForm from "./Form";

const Divider = styled(MuiDivider)(spacing);
const Button = styled(MuiButton)(spacing);

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function UtilityDetailCard(props) {
  const useStyles = style;
  const classes = useStyles();

  const canAddOrUpdate = can(props.user.details, "billingRates");

  const [display, setDisplay] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const toggleDisplay = () => {
    const shouldDisplay = !display;
    setDisplay(shouldDisplay);
  };

  function formatCurrency(s) {
    if(s)
      return "$" + s;
    else
      return 0;
  }

  return (
    <Card mb={6}>
      <CardContent xs={12}>
        <Grid container>
          <Grid item xs={6} sm={6} md={6} lg={6}>
            <Typography variant="h6" gutterBottom>
              Utilities
            </Typography>
          </Grid>
          <Grid item xs={3} sm={3} md={3} lg={3}>
            <IconButton 
              onClick={() => setExpanded(!expanded)}
              size="small"
              sx={{ 
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' }
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Grid>
          <Grid
            item
            xs={3}
            sm={3}
            md={3}
            lg={3}
            className={classes.rightSideHeaderSubMenuToolBar}
          >
            {canAddOrUpdate && (
            <Button onClick={toggleDisplay}>
              <EditIcon
                fontSize="medium"
                className={classes.SubMenuToolBarIcon}
              />
            </Button>
            )}
            <Dialog
              open={display}
              TransitionComponent={Transition}
              fullWidth={"lg"}
              maxWidth={"lg"}
              keepMounted
              onClose={toggleDisplay}
              aria-labelledby="alert-dialog-slide-title"
              aria-describedby="alert-dialog-slide-description"
            >
              {props.propertyUtilities.data.propertyUtility ? (
                <DialogTitle id="alert-dialog-slide-title">
                  Utilities {props.propertyUtilities.data.propertyUtility.name}{" "}
                </DialogTitle>
              ) : (
                <DialogTitle id="alert-dialog-slide-title">
                  Loading Utilities.....{" "}
                </DialogTitle>
              )}
              <DialogContent>
                {props.propertyUtilities.data.propertyUtility && (
                  <PropertyUtilityForm
                    onUpdate={toggleDisplay}
                    onCancel={toggleDisplay}
                    serviceOutage={
                      props.propertyUtilities.data.propertyUtility
                        .serviceOutage || 0
                    }
                    active={
                      props.propertyUtilities.data.propertyUtility.active || 0
                    }
                  />
                )}
              </DialogContent>
            </Dialog>
          </Grid>
        </Grid>
        <Divider className="mb-2 mt-2" my={1} />
        {!props.propertyUtilities.data.propertyUtility ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1.5 }} />
            ))}
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {/* Essential Information - Always Visible */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {/* Service Status */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    mb: 1,
                    display: 'block'
                  }}>
                    SERVICE STATUS
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      px: 1.5, 
                      py: 0.5, 
                      borderRadius: 1,
                      backgroundColor: props.propertyUtilities.data.propertyUtility.active == 1 ? 'success.light' : 'error.light',
                      color: props.propertyUtilities.data.propertyUtility.active == 1 ? 'success.contrastText' : 'error.contrastText'
                    }}>
                      <Typography variant="caption" fontWeight={600}>
                        {props.propertyUtilities.data.propertyUtility.active == 1 ? 'ACTIVE' : 'INACTIVE'}
                      </Typography>
                    </Box>
                    {props.propertyUtilities.data.propertyUtility.serviceOutage == 1 && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        px: 1.5, 
                        py: 0.5, 
                        borderRadius: 1,
                        backgroundColor: 'warning.light',
                        color: 'warning.contrastText'
                      }}>
                        <Typography variant="caption" fontWeight={600}>OUTAGE</Typography>
                      </Box>
                    )}
                  </Box>
                  {props.propertyUtilities.data.propertyUtility.outageReportedDate && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Outage reported:{" "}
                      {moment
                        .utc(
                          props.propertyUtilities.data.propertyUtility
                            .outageReportedDate
                        )
                        .format("LLLL")}
                    </Typography>
                  )}
                </Box>

                {/* Vendor Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em'
                  }}>
                    VENDOR
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 500, 
                    mt: 0.5,
                    color: props.propertyUtilities.data.propertyUtility.vendorName ? 'text.primary' : 'error.main'
                  }}>
                    {props.propertyUtilities.data.propertyUtility.vendorName &&
                    props.propertyUtilities.data.propertyUtility.vendorUuid ? (
                      <Link
                        to={`/vendor/${props.propertyUtilities.data.propertyUtility.vendorUuid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: 'inherit',
                          fontWeight: 500,
                          textDecoration: 'none',
                        }}
                      >
                        {props.propertyUtilities.data.propertyUtility.vendorName}
                      </Link>
                    ) : (
                      props.propertyUtilities.data.propertyUtility.vendorName || "***MISSING***"
                    )}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                {/* Account & Billing */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em'
                  }}>
                    ACCOUNT NUMBER
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 500, 
                    mt: 0.5,
                    color: 'text.primary'
                  }}>
                    {props.propertyUtilities.data.propertyUtility.vendorAcctNum || "N/A"}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em'
                  }}>
                    LAST BILL DATE
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 500, 
                    mt: 0.5,
                    color: 'text.primary'
                  }}>
                    {(props.propertyUtilities.data.propertyUtility.lastBillDate) ? 
                      new Date(props.propertyUtilities.data.propertyUtility.lastBillDate).toLocaleDateString() : 
                      "N/A"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Expandable Detailed Information */}
            <Collapse in={expanded}>
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3, mt: 2 }}>
                <Grid container spacing={4}>
                  {/* Rates & Charges Section */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                      Rates & Charges
                    </Typography>
                    
                    <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        mb: 1.5,
                        display: 'block'
                      }}>
                        STANDARD CHARGES
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Customer Charge</Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatCurrency(props.propertyUtilities.data.propertyUtility.utilCustomerCharge) || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Energy Charge</Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatCurrency(props.propertyUtilities.data.propertyUtility.utilEnergyCharge) || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Credit Card Fee</Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatCurrency(props.propertyUtilities.data.propertyUtility.creditCardFee) || "N/A"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        mb: 1.5,
                        display: 'block'
                      }}>
                        SPECIAL RATES
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Lease Light Fee</Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatCurrency(props.propertyUtilities.data.propertyUtility.leaseLightFee) || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">EV Meter Rate</Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatCurrency(props.propertyUtilities.data.propertyUtility.EVMeterRate) || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Contract Rate</Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatCurrency(props.propertyUtilities.data.propertyUtility.contractRate) || "N/A"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Settings & Configuration Section */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                      Settings & Configuration
                    </Typography>
                    
                    <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        mb: 1.5,
                        display: 'block'
                      }}>
                        CUSTOMER PERMISSIONS
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={props.propertyUtilities.data.propertyUtility.customerInitiatedMoveIns === 1}
                            disabled={true}
                            size="small"
                          />
                          <Typography variant="body2">Customer Initiated Move Ins</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={props.propertyUtilities.data.propertyUtility.customerInitiatedMoveOuts === 1}
                            disabled={true}
                            size="small"
                          />
                          <Typography variant="body2">Customer Initiated Move Outs</Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        mb: 1,
                        display: 'block'
                      }}>
                        SYSTEM INFO
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {new Date(props.propertyUtilities.data.propertyUtility.updated).toLocaleDateString() || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>

            {/* Expand/Collapse Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                onClick={() => setExpanded(!expanded)}
                variant="text"
                size="small"
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ color: 'text.secondary' }}
              >
                {expanded ? 'Show Less' : 'Show More Details'}
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}


UtilityDetailCard.propTypes = {
  apl: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  propertyUtilities: PropTypes.object.isRequired,
};

function UtilityDetailCardState(state) {
  return {
    apl: state.apl,
    propertyUtilities: state.propertyUtilities,
    user: state.user
  };
}

export default connect(UtilityDetailCardState, {})(UtilityDetailCard);
